// apps/frontend/src/components/ui/Avatar.tsx
// 🎯 Composant Avatar ULTRA-ROBUSTE - Fonctionne avec TOUTES les images

import React from 'react';

interface AvatarProps {
  src?: string;
  alt: string;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  name: string; // Pour fallback avec initiales
  className?: string;
  onClick?: () => void;
}

const Avatar: React.FC<AvatarProps> = ({
  src,
  alt,
  size = 'md',
  name,
  className = '',
  onClick,
}) => {
  // 🎯 Tailles standardisées premium
  const sizeClasses = {
    xs: 'w-6 h-6 text-xs', // 24px
    sm: 'w-8 h-8 text-sm', // 32px
    md: 'w-10 h-10 text-base', // 40px
    lg: 'w-12 h-12 text-lg', // 48px
    xl: 'w-16 h-16 text-xl', // 64px
  };

  const sizeStyles = {
    xs: { width: '24px', height: '24px' },
    sm: { width: '32px', height: '32px' },
    md: { width: '40px', height: '40px' },
    lg: { width: '48px', height: '48px' },
    xl: { width: '64px', height: '64px' },
  };

  // 🎯 Couleurs premium cohérentes avec votre design beige
  const getBackgroundColor = (name: string) => {
    const colors = [
      'var(--brand-500)', // Beige principal
      '#d4af7a', // Beige clair
      'var(--brand-sage)', // Beige foncé
      '#a68b72', // Marron clair
      '#94796f', // Marron moyen
      '#82676c', // Marron foncé
      '#70556a', // Prune
      'var(--brand-warm)', // Kaki
    ];
    const index = name.charCodeAt(0) % colors.length;
    return colors[index];
  };

  // 🎯 Style universel ULTRA-ROBUSTE
  const avatarStyle = {
    ...sizeStyles[size],
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    flexShrink: 0,
    cursor: onClick ? 'pointer' : 'default',
    userSelect: 'none' as const,
    position: 'relative' as const,
  };

  // 🎯 SOLUTION ULTRA-ROBUSTE : Double conteneur pour gérer TOUTES les images
  if (src) {
    return (
      <div
        className={`${sizeClasses[size]} ${className}`}
        style={avatarStyle}
        onClick={onClick}
        title={alt}
        role={onClick ? 'button' : 'img'}
        aria-label={alt}
      >
        <div
          style={{
            width: '100%',
            height: '100%',
            backgroundImage: `url("${src}")`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            borderRadius: '50%',
          }}
          onError={e => {
            // Fallback en cas d'erreur de chargement
            const target = e.target as HTMLImageElement;
            target.style.display = 'none';
            const parent = target.parentElement;
            if (parent) {
              parent.innerHTML = `<div style="width: 100%; height: 100%; display: flex; align-items: center; justify-content: center; background-color: ${getBackgroundColor(name)}; color: white; font-weight: 600;">${name.charAt(0).toUpperCase()}</div>`;
            }
          }}
        />
      </div>
    );
  }

  // 🎯 Fallback premium avec initiales colorées
  return (
    <div
      className={`${sizeClasses[size]} ${className} font-semibold text-white`}
      style={{
        ...avatarStyle,
        backgroundColor: getBackgroundColor(name),
      }}
      onClick={onClick}
      title={alt}
      role={onClick ? 'button' : 'img'}
      aria-label={alt}
    >
      {name.charAt(0).toUpperCase()}
    </div>
  );
};

export default Avatar;
